/**
 * Wallet Hooks
 * Manages deposit and withdrawal flows for the wallet page
 *
 * Flow:
 * 1. Check for pending deposit on mount (getPendingDeposit)
 * 2. If no pending → show calculator → initiateDeposit
 * 3. If pending → show payment details → poll status or use WebSocket
 * 4. If status=paid → show receipt upload → submitReceipt
 * 5. If status=completed → show success
 *
 * Withdrawal is simpler: initiateWithdrawal → show success message
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi, useMutation } from './useApi';
import { walletAPI, mediaAPI } from '@/services/api';
import type {
  PendingDepositResponse,
  PendingDepositInfo,
  DepositInitResponse,
  DepositStatusResponse,
  DepositCheckResponse,
  DepositReceiptResponse,
  WithdrawalInitResponse,
} from '@/types';
import type { DepositPhase } from '@/types/domain';
import { DEFAULT_DEPOSIT_POLLING, type DepositPollingConfig } from '@club-bot/shared/contracts';

// =============================================================================
// Pending Deposit Hook
// =============================================================================

export interface UsePendingDepositOptions {
  fetchOnMount?: boolean;
  onSuccess?: (data: PendingDepositResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for checking pending deposit on app load
 *
 * @example
 * const { data, isLoading, refetch } = usePendingDeposit({ fetchOnMount: true });
 * if (data?.hasPending) {
 *   // Show existing deposit flow
 * }
 */
export function usePendingDeposit(options: UsePendingDepositOptions = {}) {
  const { fetchOnMount = true, onSuccess, onError } = options;

  const api = useApi<PendingDepositResponse>(
    () => walletAPI.getPendingDeposit(),
    { fetchOnMount, onSuccess, onError }
  );

  return {
    ...api,
    hasPending: api.data?.hasPending ?? false,
    // Access deposit only when hasPending is true (discriminated union)
    pendingDeposit: api.data?.hasPending ? api.data.deposit : null,
    refetch: api.execute,
  };
}

// =============================================================================
// Deposit Flow Hook
// =============================================================================

export interface UseDepositFlowOptions {
  onDepositCreated?: (data: DepositInitResponse) => void;
  onDepositPaid?: (data: DepositCheckResponse) => void;
  onDepositCompleted?: (data: DepositReceiptResponse) => void;
  onError?: (error: string) => void;
  onPollingTimeout?: () => void;
  /** Polling configuration (uses DEFAULT_DEPOSIT_POLLING if not provided) */
  pollingConfig?: Partial<DepositPollingConfig>;
}

/**
 * Complete deposit flow hook
 * Manages: initiate → poll → receipt upload → complete
 *
 * Polling behavior:
 * - Auto-starts when phase is 'waiting'
 * - Uses exponential backoff (configurable)
 * - Stops on status change, max attempts, or deposit expiry
 */
export function useDepositFlow(options: UseDepositFlowOptions = {}) {
  const {
    onDepositCreated,
    onDepositPaid,
    onDepositCompleted,
    onError,
    onPollingTimeout,
    pollingConfig: userPollingConfig,
  } = options;

  // Merge user config with defaults
  const pollingConfig: DepositPollingConfig = {
    ...DEFAULT_DEPOSIT_POLLING,
    ...userPollingConfig,
  };

  const [phase, setPhase] = useState<DepositPhase>('idle');
  const [currentDeposit, setCurrentDeposit] = useState<DepositInitResponse | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollAttemptRef = useRef<number>(0);
  const currentIntervalRef = useRef<number>(pollingConfig.intervalMs);

  // Initiate deposit mutation
  const initiateMutation = useMutation<DepositInitResponse, [number]>(
    (amount) => walletAPI.initiateDeposit({ amount }),
    {
      onSuccess: (data) => {
        setCurrentDeposit(data);
        setPhase('waiting');
        onDepositCreated?.(data);
      },
      onError,
    }
  );

  // Check status mutation
  const checkStatusMutation = useMutation<DepositCheckResponse, [string]>(
    (ref) => walletAPI.checkDepositStatus(ref),
    {
      onSuccess: (data) => {
        if (data.status === 'paid') {
          setPhase('upload_receipt');
          stopPolling();
          onDepositPaid?.(data);
        } else if (data.status === 'completed') {
          setPhase('done');
          stopPolling();
        }
      },
    }
  );

  // Submit receipt mutation
  const submitReceiptMutation = useMutation<DepositReceiptResponse, [string, string]>(
    (ref, fileId) => walletAPI.submitReceipt(ref, { fileId }),
    {
      onSuccess: (data) => {
        setPhase('bonus-flying');
        onDepositCompleted?.(data);
      },
      onError,
    }
  );

  // Cancel deposit mutation
  const cancelMutation = useMutation<{ message: string }, [string]>(
    (ref) => walletAPI.cancelDeposit(ref)
  );

  // Polling logic with exponential backoff
  const stopPolling = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    setIsPolling(false);
    // Reset for next time
    pollAttemptRef.current = 0;
    currentIntervalRef.current = pollingConfig.intervalMs;
  }, [pollingConfig.intervalMs]);

  const scheduleNextPoll = useCallback(() => {
    if (!currentDeposit?.referenceNumber) return;

    // Check if we've exceeded max attempts
    if (pollAttemptRef.current >= pollingConfig.maxAttempts) {
      console.warn('[useDepositFlow] Max poll attempts reached, stopping');
      stopPolling();
      onPollingTimeout?.();
      return;
    }

    // Check if deposit has expired
    if (currentDeposit.expiresAt) {
      const expiresAt = new Date(currentDeposit.expiresAt);
      if (expiresAt < new Date()) {
        console.warn('[useDepositFlow] Deposit expired, stopping polling');
        stopPolling();
        onPollingTimeout?.();
        return;
      }
    }

    // Schedule next poll with current interval
    pollTimeoutRef.current = setTimeout(async () => {
      if (!currentDeposit?.referenceNumber) return;

      pollAttemptRef.current += 1;

      // Execute the status check
      await checkStatusMutation.mutate(currentDeposit.referenceNumber);

      // Apply backoff for next interval (capped at maxIntervalMs)
      currentIntervalRef.current = Math.min(
        currentIntervalRef.current * pollingConfig.backoffMultiplier,
        pollingConfig.maxIntervalMs
      );

      // Schedule next poll (if still polling - status change will call stopPolling)
      if (isPolling) {
        scheduleNextPoll();
      }
    }, currentIntervalRef.current);
  }, [
    currentDeposit?.referenceNumber,
    currentDeposit?.expiresAt,
    pollingConfig.maxAttempts,
    pollingConfig.backoffMultiplier,
    pollingConfig.maxIntervalMs,
    checkStatusMutation,
    isPolling,
    stopPolling,
    onPollingTimeout,
  ]);

  const startPolling = useCallback(() => {
    if (!currentDeposit?.referenceNumber || isPolling) return;

    console.log('[useDepositFlow] Starting polling with config:', pollingConfig);
    setIsPolling(true);
    pollAttemptRef.current = 0;
    currentIntervalRef.current = pollingConfig.intervalMs;

    // Do an immediate check, then schedule subsequent polls
    checkStatusMutation.mutate(currentDeposit.referenceNumber);
    scheduleNextPoll();
  }, [currentDeposit?.referenceNumber, isPolling, pollingConfig, checkStatusMutation, scheduleNextPoll]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // Auto-start polling when in 'waiting' phase
  useEffect(() => {
    if (phase === 'waiting' && currentDeposit?.referenceNumber && !isPolling) {
      console.log('[useDepositFlow] Auto-starting polling for waiting phase');
      startPolling();
    }
    // Stop polling if phase changes away from 'waiting'
    if (phase !== 'waiting' && isPolling) {
      console.log('[useDepositFlow] Phase changed from waiting, stopping polling');
      stopPolling();
    }
  }, [phase, currentDeposit?.referenceNumber, isPolling, startPolling, stopPolling]);

  // Upload photo helper
  const uploadAndSubmitReceipt = useCallback(
    async (file: File) => {
      if (!currentDeposit?.referenceNumber) return null;

      setPhase('uploading');
      try {
        const uploadResult = await mediaAPI.uploadPhoto(
          file,
          'deposit',
          currentDeposit.referenceNumber
        );
        const result = await submitReceiptMutation.mutate(
          currentDeposit.referenceNumber,
          uploadResult.fileId
        );
        return result;
      } catch (err) {
        setPhase('upload_receipt'); // Go back to upload phase
        onError?.((err as Error).message);
        return null;
      }
    },
    [currentDeposit?.referenceNumber, submitReceiptMutation, onError]
  );

  // Actions
  const initiateDeposit = useCallback(
    async (amount: number) => {
      return initiateMutation.mutate(amount);
    },
    [initiateMutation]
  );

  const checkStatus = useCallback(() => {
    if (currentDeposit?.referenceNumber) {
      return checkStatusMutation.mutate(currentDeposit.referenceNumber);
    }
    return Promise.resolve(null);
  }, [currentDeposit?.referenceNumber, checkStatusMutation]);

  const cancelDeposit = useCallback(async () => {
    if (currentDeposit?.referenceNumber) {
      const result = await cancelMutation.mutate(currentDeposit.referenceNumber);
      if (result) {
        stopPolling();
        setPhase('idle');
        setCurrentDeposit(null);
      }
      return result;
    }
    return null;
  }, [currentDeposit?.referenceNumber, cancelMutation, stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    setPhase('idle');
    setCurrentDeposit(null);
    initiateMutation.reset();
    checkStatusMutation.reset();
    submitReceiptMutation.reset();
    cancelMutation.reset();
  }, [stopPolling, initiateMutation, checkStatusMutation, submitReceiptMutation, cancelMutation]);

  // Resume from pending deposit
  const resumeFromPending = useCallback(
    (pendingDeposit: PendingDepositInfo | null) => {
      if (!pendingDeposit) return;

      setCurrentDeposit({
        referenceNumber: pendingDeposit.referenceNumber,
        dealId: pendingDeposit.dealId || '',
        method: '',
        amount: pendingDeposit.amount,
        bonusPercentage: pendingDeposit.bonusPercentage,
        bonusAmount: pendingDeposit.bonusAmount,
        expiresAt: pendingDeposit.expiresAt,
        cardNumber: pendingDeposit.cardNumber,
        cardHolderName: pendingDeposit.cardHolderName,
        bank: pendingDeposit.bank,
        paymentUrl: null, // Not available when resuming from pending
      });

      if (pendingDeposit.status === 'paid') {
        setPhase('upload_receipt');
      } else {
        setPhase('waiting');
      }
    },
    []
  );

  // Get current poll attempt count (for UI display if needed)
  const getPollAttempts = useCallback(() => pollAttemptRef.current, []);

  return {
    // State
    phase,
    currentDeposit,
    isPolling,

    // Loading states
    isInitiating: initiateMutation.isLoading,
    isCheckingStatus: checkStatusMutation.isLoading,
    isUploadingReceipt: submitReceiptMutation.isLoading,
    isCancelling: cancelMutation.isLoading,

    // Errors
    initiateError: initiateMutation.error,
    checkStatusError: checkStatusMutation.error,
    receiptError: submitReceiptMutation.error,
    cancelError: cancelMutation.error,

    // Actions
    initiateDeposit,
    checkStatus,
    uploadAndSubmitReceipt,
    cancelDeposit,
    startPolling,
    stopPolling,
    resumeFromPending,
    reset,

    // Phase setters (for animations)
    setPhase,

    // Polling info (for debugging/UI)
    getPollAttempts,
    pollingConfig,
  };
}

// =============================================================================
// Deposit Status Hook (Simple polling)
// =============================================================================

export interface UseDepositStatusOptions {
  referenceNumber: string | null;
  enabled?: boolean;
  pollInterval?: number;
  onStatusChange?: (status: DepositStatusResponse) => void;
}

/**
 * Hook for polling deposit status
 */
export function useDepositStatus(options: UseDepositStatusOptions) {
  const { referenceNumber, enabled = true, pollInterval = 3000, onStatusChange } = options;

  const [status, setStatus] = useState<DepositStatusResponse | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const api = useApi<DepositStatusResponse, [string]>(
    (ref) => walletAPI.getDepositStatus(ref),
    {
      onSuccess: (data) => {
        setStatus(data);
        onStatusChange?.(data);
      },
    }
  );

  useEffect(() => {
    if (!referenceNumber || !enabled) return;

    // Initial fetch
    api.execute(referenceNumber);

    // Start polling
    intervalRef.current = setInterval(() => {
      api.execute(referenceNumber);
    }, pollInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [referenceNumber, enabled, pollInterval]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    status,
    isLoading: api.isLoading,
    error: api.error,
    refetch: () => referenceNumber && api.execute(referenceNumber),
  };
}

// =============================================================================
// Withdrawal Hook
// =============================================================================

export interface UseWithdrawalOptions {
  onSuccess?: (data: WithdrawalInitResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for initiating withdrawals
 *
 * @example
 * const { initiateWithdrawal, isLoading, data } = useWithdrawal();
 * const result = await initiateWithdrawal(50, '4111111111111111');
 */
export function useWithdrawal(options: UseWithdrawalOptions = {}) {
  const mutation = useMutation<WithdrawalInitResponse, [number, string]>(
    (amount, cardNumber) => walletAPI.initiateWithdrawal({ amount, cardNumber }),
    options
  );

  return {
    ...mutation,
    initiateWithdrawal: mutation.mutate,
  };
}

// =============================================================================
// Combined Wallet Hook
// =============================================================================

export interface UseWalletOptions {
  onWithdrawalError?: (error: string) => void;
}

/**
 * All-in-one wallet hook for simple use cases
 */
export function useWallet(options: UseWalletOptions = {}) {
  const pendingDeposit = usePendingDeposit({ fetchOnMount: true });
  const depositFlow = useDepositFlow();
  const withdrawal = useWithdrawal({ onError: options.onWithdrawalError });

  // Auto-resume if there's a pending deposit
  useEffect(() => {
    if (pendingDeposit.hasPending && pendingDeposit.pendingDeposit) {
      depositFlow.resumeFromPending(pendingDeposit.pendingDeposit);
    }
  }, [pendingDeposit.hasPending, pendingDeposit.pendingDeposit]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // Pending deposit check
    isCheckingPending: pendingDeposit.isLoading,
    hasPendingDeposit: pendingDeposit.hasPending,
    pendingDeposit: pendingDeposit.pendingDeposit,

    // Deposit flow
    depositPhase: depositFlow.phase,
    currentDeposit: depositFlow.currentDeposit,
    initiateDeposit: depositFlow.initiateDeposit,
    checkDepositStatus: depositFlow.checkStatus,
    uploadReceipt: depositFlow.uploadAndSubmitReceipt,
    cancelDeposit: depositFlow.cancelDeposit,
    startPolling: depositFlow.startPolling,
    stopPolling: depositFlow.stopPolling,
    resetDeposit: depositFlow.reset,
    setDepositPhase: depositFlow.setPhase,

    // Deposit loading states
    isInitiatingDeposit: depositFlow.isInitiating,
    isUploadingReceipt: depositFlow.isUploadingReceipt,

    // Withdrawal
    initiateWithdrawal: withdrawal.initiateWithdrawal,
    isInitiatingWithdrawal: withdrawal.isLoading,
    withdrawalResult: withdrawal.data,
    withdrawalError: withdrawal.error,

    // General
    refetchPending: pendingDeposit.refetch,
  };
}
